export type BookingDetailOrigin =
  | 'enquiries'
  | 'bookings'
  | 'cancelled'
  | 'payments'
  | 'history';

export interface BookingNavigationState {
  from?: BookingDetailOrigin;
  returnPath?: string;
  enquiryTab?: string;
  cancelledTab?: string;
}

const ORIGIN_RETURN_PATH: Record<BookingDetailOrigin, string> = {
  enquiries: '/enquires',
  bookings: '/bookings',
  cancelled: '/cancelled',
  payments: '/payments',
  history: '/enquires',
};

export const buildBookingDetailState = (
  origin: BookingDetailOrigin,
  options?: {
    enquiryTab?: string;
    cancelledTab?: string;
    returnPath?: string;
  },
): BookingNavigationState => ({
  from: origin,
  returnPath: options?.returnPath ?? ORIGIN_RETURN_PATH[origin],
  enquiryTab: options?.enquiryTab,
  cancelledTab: options?.cancelledTab,
});

// Static fallback for detail routes that don't (or can't) pass navigation
// state carrying a returnPath — e.g. plain `navigate(path)` calls with no
// second argument. Checked longest-prefix-first so nested routes don't get
// shadowed by a shorter sibling prefix.
const DETAIL_ROUTE_RETURN_PATHS: [string, string][] = (
  [
    ['/amc/add-quotation', '/request/amc'],
    ['/amc/request-details', '/request/amc'],
    ['/purchase/lead-details', '/purchase'],
  ] as [string, string][]
).sort((a, b) => b[0].length - a[0].length);

const staticReturnPathFor = (pathname: string): string | null => {
  const match = DETAIL_ROUTE_RETURN_PATHS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : null;
};

/**
 * Resolves which sidebar item should be highlighted for the current route.
 * List pages that navigate to a detail view can pass `{ returnPath }` in
 * router state (see buildBookingDetailState / QuoteRequestList) so the
 * sidebar keeps highlighting the list you came from instead of going dark
 * on a detail page with no matching menu entry. Routes that can't pass
 * state (e.g. a plain `navigate(path)`) fall back to the static map above.
 */
export const getActiveMenuPath = (
  pathname: string,
  state: unknown,
): string => {
  const navState = state as BookingNavigationState | null | undefined;

  if (navState && typeof navState === 'object' && navState.returnPath) {
    return navState.returnPath;
  }

  return staticReturnPathFor(pathname) ?? pathname;
};

export const getAssignBookingHeaderTitle = (state: unknown): string => {
  const navState = state as BookingNavigationState | null | undefined;
  if (navState?.from === 'payments') {
    return 'Payment Details';
  }
  return 'Assign Technician';
};

export const getBookingBackTarget = (
  state: unknown,
): { path: string; replace: boolean } | null => {
  const navState = state as BookingNavigationState | null | undefined;
  if (!navState?.returnPath) {
    return null;
  }

  const params = new URLSearchParams();

  // '' is the "All Enquires" tab's real value (see Enquiry/index.tsx), so
  // this must check presence, not truthiness — a truthy check silently drops
  // it and falls back to the default "Today Enquires" tab.
  if (navState.enquiryTab !== undefined) {
    params.set('tab', navState.enquiryTab);
  }

  if (navState.cancelledTab) {
    params.set('tab', navState.cancelledTab);
  }

  const query = params.toString();
  return {
    path: query ? `${navState.returnPath}?${query}` : navState.returnPath,
    replace: false,
  };
};
